import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent } from './ui/dialog';
import { CheckCircle, Users, Briefcase, CheckSquare, BarChart3, FileText, ArrowRight, ArrowLeft, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnboardingWizard = ({ user, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const steps = [
    {
      title: "Welcome to GSTAssist! 🎉",
      description: "Let's get you started with your GST & Financial Management platform",
      icon: CheckCircle,
      color: "from-blue-500 to-indigo-600",
      content: (
        <div className="space-y-4">
          <p className="text-lg text-gray-700">
            Hi <span className="font-bold text-orange-600">{user?.name}</span>! Welcome to your complete GST and financial management solution.
          </p>
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-6 space-y-3">
            <h4 className="font-semibold text-gray-900">What you can do:</h4>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>Manage clients and their GST details</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>Create and track tasks with automation</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>Generate invoices and manage receipts</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>Track expenses and financial reports</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>Collaborate with your team</span>
              </li>
            </ul>
          </div>
          <p className="text-sm text-gray-600 mt-4">
            This quick tour will help you understand where to start. It will only take 2 minutes!
          </p>
        </div>
      )
    },
    {
      title: "Step 1: Add Your Clients",
      description: "Start by adding clients you work with",
      icon: Users,
      color: "from-green-500 to-emerald-600",
      content: (
        <div className="space-y-4">
          <div className="bg-green-50 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-3">Why add clients first?</h4>
            <p className="text-gray-700 mb-4">
              Clients are at the heart of GSTAssist. Once you add clients, you can:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-600 font-bold mr-2">→</span>
                <span>Track their GST registration details</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 font-bold mr-2">→</span>
                <span>Create tasks specific to each client</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 font-bold mr-2">→</span>
                <span>Generate invoices and track payments</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 font-bold mr-2">→</span>
                <span>Store important documents</span>
              </li>
            </ul>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Quick Tip:</strong> You can add client details like GSTIN, PAN, contact info, and billing address.
            </p>
          </div>
          <Button 
            onClick={() => {
              onComplete();
              navigate('/clients');
            }}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
          >
            Go to Clients Page
          </Button>
        </div>
      )
    },
    {
      title: "Step 2: Set Up Services",
      description: "Define the services you offer to clients",
      icon: Briefcase,
      color: "from-purple-500 to-pink-600",
      content: (
        <div className="space-y-4">
          <div className="bg-purple-50 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-3">What are Services?</h4>
            <p className="text-gray-700 mb-4">
              Services are the professional services you provide to clients, such as:
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-lg p-3 border border-purple-200">
                <p className="text-sm font-semibold text-purple-900">GST Filing</p>
                <p className="text-xs text-gray-600">GSTR-1, GSTR-3B</p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-purple-200">
                <p className="text-sm font-semibold text-purple-900">Tax Planning</p>
                <p className="text-xs text-gray-600">Advisory services</p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-purple-200">
                <p className="text-sm font-semibold text-purple-900">Bookkeeping</p>
                <p className="text-xs text-gray-600">Monthly records</p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-purple-200">
                <p className="text-sm font-semibold text-purple-900">Audit Support</p>
                <p className="text-xs text-gray-600">Documentation</p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Pro Tip:</strong> Set default pricing for services to speed up invoice creation!
            </p>
          </div>
          <Button 
            onClick={() => {
              onComplete();
              navigate('/services');
            }}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
          >
            Go to Services Page
          </Button>
        </div>
      )
    },
    {
      title: "Step 3: Create & Automate Tasks",
      description: "Never miss a deadline with task automation",
      icon: CheckSquare,
      color: "from-orange-500 to-red-500",
      content: (
        <div className="space-y-4">
          <div className="bg-orange-50 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-3">Powerful Task Management</h4>
            <p className="text-gray-700 mb-4">
              GSTAssist helps you stay on top of deadlines with:
            </p>
            <div className="space-y-3">
              <div className="bg-white rounded-lg p-4 border-l-4 border-orange-500">
                <p className="font-semibold text-gray-900">Manual Tasks</p>
                <p className="text-sm text-gray-600">Create one-time or specific tasks for clients</p>
              </div>
              <div className="bg-white rounded-lg p-4 border-l-4 border-red-500">
                <p className="font-semibold text-gray-900">Task Templates</p>
                <p className="text-sm text-gray-600">Set up recurring tasks (e.g., monthly GSTR-1) that auto-generate</p>
              </div>
              <div className="bg-white rounded-lg p-4 border-l-4 border-yellow-500">
                <p className="font-semibold text-gray-900">Auto-Assignment</p>
                <p className="text-sm text-gray-600">Automatically assign tasks to staff based on roles</p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Getting Started:</strong> Create your first task manually, then explore Task Templates for automation!
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button 
              onClick={() => {
                onComplete();
                navigate('/tasks');
              }}
              variant="outline"
              className="border-2 border-orange-300"
            >
              View Tasks
            </Button>
            <Button 
              onClick={() => {
                onComplete();
                navigate('/task-templates');
              }}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              Task Templates
            </Button>
          </div>
        </div>
      )
    },
    {
      title: "Step 4: Explore Your Dashboard",
      description: "Get a complete overview of your business",
      icon: BarChart3,
      color: "from-blue-500 to-cyan-600",
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-3">Your Command Center</h4>
            <p className="text-gray-700 mb-4">
              The dashboard gives you a bird&apos;s-eye view of:
            </p>
            <div className="grid grid-cols-1 gap-3">
              <div className="bg-white rounded-lg p-4 flex items-start">
                <BarChart3 className="w-6 h-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Financial Overview</p>
                  <p className="text-sm text-gray-600">Total revenue, pending amounts, and trends</p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 flex items-start">
                <CheckSquare className="w-6 h-6 text-green-600 mr-3 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Task Summary</p>
                  <p className="text-sm text-gray-600">Pending, in-progress, and completed tasks</p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 flex items-start">
                <Users className="w-6 h-6 text-purple-600 mr-3 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Client Insights</p>
                  <p className="text-sm text-gray-600">Active clients and quick access to details</p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 flex items-start">
                <FileText className="w-6 h-6 text-orange-600 mr-3 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Recent Activity</p>
                  <p className="text-sm text-gray-600">Latest invoices, receipts, and updates</p>
                </div>
              </div>
            </div>
          </div>
          <Button 
            onClick={() => {
              onComplete();
              navigate('/dashboard');
            }}
            className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
          >
            Go to Dashboard
          </Button>
        </div>
      )
    },
    {
      title: "You're All Set! 🚀",
      description: "Start managing your GST & finances efficiently",
      icon: CheckCircle,
      color: "from-green-500 to-emerald-600",
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-3 text-xl">Quick Reference Guide</h4>
            <div className="space-y-3">
              <div className="bg-white rounded-lg p-4">
                <p className="font-semibold text-gray-900 mb-2">📋 Recommended Flow:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                  <li>Add clients → Add services</li>
                  <li>Set up task templates for recurring work</li>
                  <li>Create tasks or let templates auto-generate them</li>
                  <li>Generate invoices for completed work</li>
                  <li>Track receipts and expenses</li>
                  <li>Monitor everything from your dashboard</li>
                </ol>
              </div>
              
              <div className="bg-white rounded-lg p-4">
                <p className="font-semibold text-gray-900 mb-2">💡 Power Features:</p>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• <strong>Staff Management:</strong> Add team members and assign tasks</li>
                  <li>• <strong>Documents:</strong> Upload and organize client documents</li>
                  <li>• <strong>Communications:</strong> Track all client interactions</li>
                  <li>• <strong>Reports:</strong> Generate financial reports anytime</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Need Help?</strong> You can access this tour anytime from your profile settings.
            </p>
          </div>
          
          <Button 
            onClick={() => {
              onComplete();
              navigate('/dashboard');
            }}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 h-12 text-lg"
          >
            Start Using GSTAssist
          </Button>
        </div>
      )
    }
  ];

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onSkip()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <div className="relative">
          {/* Close Button */}
          <button
            onClick={onSkip}
            className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Progress Bar */}
          <div className="h-2 bg-gray-200">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>

          {/* Header */}
          <div className={`bg-gradient-to-r ${currentStepData.color} text-white p-8`}>
            <div className="flex items-center mb-4">
              <div className="bg-white/20 rounded-full p-3 mr-4">
                <Icon className="w-8 h-8" />
              </div>
              <div>
                <p className="text-sm opacity-90">Step {currentStep + 1} of {steps.length}</p>
                <h2 className="text-2xl font-bold">{currentStepData.title}</h2>
              </div>
            </div>
            <p className="text-white/90">{currentStepData.description}</p>
          </div>

          {/* Content */}
          <div className="p-8">
            {currentStepData.content}
          </div>

          {/* Navigation */}
          <div className="border-t border-gray-200 p-6 bg-gray-50 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentStep 
                      ? 'w-8 bg-orange-500' 
                      : index < currentStep 
                      ? 'w-2 bg-green-500' 
                      : 'w-2 bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <div className="flex items-center space-x-3">
              {currentStep > 0 && (
                <Button
                  onClick={handlePrevious}
                  variant="outline"
                  className="border-2"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
              )}
              
              {currentStep < steps.length - 1 ? (
                <Button
                  onClick={handleNext}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={onSkip}
                  variant="outline"
                  className="border-2 border-gray-300"
                >
                  Skip Tour
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;
